/** Column-header sorting for rendered tables. Reset filters restores the default row order. */

type SortDir = "asc" | "desc";

type HeaderSort = { key: string; dir: SortDir };

const sorts = new Map<string, HeaderSort>();
let applying = false;
let reapplyQueued = false;
let pointerMoved = false;
let pointerX = 0;
let pointerY = 0;
let mounted = false;

const FOLLOWER_ROW =
  "tr.sfe-hist-linked-detail, tr.sfe-stocks-share-history-row, tr.sfe-hist-linked-detail-row";

function storeKey(table: HTMLTableElement): string {
  const floated = table.dataset.sfeFloatFor;
  if (floated) return `id:${floated}`;
  if (table.id) return `id:${table.id}`;
  if (table.classList.contains("sfe-income-txn-table")) return "class:sfe-income-txn-table";
  if (table.classList.contains("sfe-calls-plus-table-head")) return "class:sfe-calls-plus-table-head";
  if (table.classList.contains("sfe-calls-plus-calls-history-table")) return "class:sfe-calls-plus-calls-history-table";
  if (table.classList.contains("sfe-calls-plus-ledger-table")) return "class:sfe-calls-plus-ledger-table";
  const marker = [...table.classList].find((name) => name.startsWith("sfe-") || name.startsWith("wheel-"));
  const scope = table.closest("[data-wheel-view]");
  const scopeId = scope instanceof HTMLElement ? (scope.dataset.wheelView ?? "page") : "page";
  const token = marker ?? "table";
  const peers = [...document.querySelectorAll(`table.${token}`)].filter(
    (node) => node instanceof HTMLTableElement && node.closest("[data-wheel-view]") === scope,
  );
  const index = Math.max(0, peers.indexOf(table));
  return `${scopeId}:${token}:${index}`;
}

function sourceTable(node: Element): HTMLTableElement | null {
  const table = node.closest("table");
  if (!(table instanceof HTMLTableElement)) return null;
  const floated = table.dataset.sfeFloatFor;
  if (!floated) return table;
  const source = document.getElementById(floated);
  return source instanceof HTMLTableElement ? source : table;
}

function sortKeyFromHeader(th: HTMLTableCellElement): string {
  return th.dataset.sort || th.dataset.col || "";
}

function skipHeader(th: HTMLTableCellElement): boolean {
  const key = sortKeyFromHeader(th);
  return !key || key === "actions" || key === "_actions" || key === "menu";
}

function columnType(th: HTMLTableCellElement): "num" | "str" {
  const type = th.dataset.type ?? "";
  if (type === "num" || type === "number") return "num";
  if (th.classList.contains("num")) return "num";
  return "str";
}

function headerFor(table: HTMLTableElement, key: string): HTMLTableCellElement | null {
  const match = [...table.querySelectorAll("thead th")].find((th) => {
    if (!(th instanceof HTMLTableCellElement)) return false;
    return sortKeyFromHeader(th) === key;
  });
  return match instanceof HTMLTableCellElement ? match : null;
}

function tablesForKey(key: string): HTMLTableElement[] {
  const found: HTMLTableElement[] = [];
  for (const node of document.querySelectorAll("table")) {
    if (!(node instanceof HTMLTableElement)) continue;
    if (node.dataset.sfeFloatFor) continue;
    if (storeKey(node) === key) found.push(node);
  }
  return found;
}

function parseSortText(raw: string, type: "num" | "str"): number | string | null {
  const line = raw.split("\n")[0]?.replace(/\s+/g, " ").trim() ?? "";
  if (!line || line === "—" || line === "-" || line === "–" || line === "N/A") return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(line)) return line.slice(0, 10);
  if (type === "num") {
    const negative = line.startsWith("-") || line.startsWith("−") || /^\(.*\)$/.test(line);
    const cleaned = line.replace(/[−,()$%\s]/g, "").replace(/^\+/, "").replace(/[^\d.-]/g, "");
    const value = Number(cleaned);
    if (!Number.isFinite(value)) return null;
    return negative && value > 0 ? -value : value;
  }
  return line.toLocaleLowerCase();
}

function cellText(row: HTMLElement, key: string): string {
  const cell =
    row.querySelector<HTMLElement>(`td[data-sort="${key}"], td[data-col="${key}"]`) ??
    row.querySelector<HTMLElement>(`td[data-sort="${key}"]`);
  if (!cell) return "";
  const stamped = cell.dataset.sortValue;
  if (stamped) return stamped;
  return cell.innerText ?? "";
}

function compareValues(left: number | string | null, right: number | string | null, dir: SortDir): number {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  let cmp = 0;
  if (typeof left === "number" && typeof right === "number") cmp = left - right;
  else cmp = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
  return dir === "asc" ? cmp : -cmp;
}

function stampOrder(nodes: HTMLElement[]): void {
  nodes.forEach((node, index) => {
    if (!node.dataset.sfeSortOrder) node.dataset.sfeSortOrder = String(index);
  });
}

function byDefaultOrder(left: HTMLElement, right: HTMLElement): number {
  return Number(left.dataset.sfeSortOrder ?? 0) - Number(right.dataset.sfeSortOrder ?? 0);
}

type RowUnit = { row: HTMLTableRowElement; followers: HTMLTableRowElement[] };

function rowUnits(tbody: HTMLTableSectionElement): { sortable: RowUnit[]; pinned: HTMLTableRowElement[] } {
  const sortable: RowUnit[] = [];
  const pinned: HTMLTableRowElement[] = [];
  for (const row of [...tbody.rows]) {
    if (row.matches(FOLLOWER_ROW)) {
      const last = sortable[sortable.length - 1];
      if (last) last.followers.push(row);
      else pinned.push(row);
      continue;
    }
    const only = row.cells.length === 1 && row.cells[0]?.colSpan && row.cells[0].colSpan > 1;
    if (only) pinned.push(row);
    else sortable.push({ row, followers: [] });
  }
  return { sortable, pinned };
}

function paintHeaders(table: HTMLTableElement, sort: HeaderSort | null): void {
  const tables = [table];
  if (table.id) {
    const float = document.querySelector(`table[data-sfe-float-for="${CSS.escape(table.id)}"]`);
    if (float instanceof HTMLTableElement) tables.push(float);
  }
  for (const host of tables) {
    for (const th of host.querySelectorAll("thead th")) {
      if (!(th instanceof HTMLTableCellElement)) continue;
      const key = sortKeyFromHeader(th);
      if (!key || skipHeader(th)) {
        clearSortIndicator(th);
        th.removeAttribute("aria-sort");
        continue;
      }
      const active = sort?.key === key;
      th.setAttribute("aria-sort", active ? (sort.dir === "asc" ? "ascending" : "descending") : "none");
      if (!active || !sort) {
        clearSortIndicator(th);
        continue;
      }
      attachSortIndicator(th, sort.dir);
    }
  }
}

export function attachSortIndicator(th: HTMLTableCellElement, direction: "asc" | "desc"): void {
  clearSortIndicator(th);
  const mark = document.createElement("span");
  mark.className = "sort-ind";
  mark.textContent = direction === "asc" ? "↑" : "↓";
  const inner = th.querySelector(":scope > .th-inner");
  (inner ?? th).appendChild(mark);
}

function clearSortIndicator(th: HTMLTableCellElement): void {
  th.querySelector(".sort-ind")?.remove();
}

function headersPainted(table: HTMLTableElement, sort: HeaderSort | null): boolean {
  const th = table.querySelector("thead th[aria-sort='ascending'], thead th[aria-sort='descending']");
  if (!sort) return !th;
  if (!(th instanceof HTMLTableCellElement)) return false;
  return sortKeyFromHeader(th) === sort.key && th.getAttribute("aria-sort") === (sort.dir === "asc" ? "ascending" : "descending");
}

function sortTableRows(table: HTMLTableElement, sort: HeaderSort | null): void {
  if (table.id === "wheelPositionsTable") return;
  if (table.classList.contains("sfe-calls-plus-table-head")) {
    sortCallsPlusGroups(table, sort);
    return;
  }
  const tbody = table.tBodies[0];
  if (!tbody) return;
  const { sortable, pinned } = rowUnits(tbody);
  stampOrder(sortable.map((unit) => unit.row));
  const type = sort ? columnType(headerFor(table, sort.key) ?? document.createElement("th")) : "str";
  const ordered = [...sortable];
  if (sort) {
    ordered.sort((left, right) => {
      const cmp = compareValues(
        parseSortText(cellText(left.row, sort.key), type),
        parseSortText(cellText(right.row, sort.key), type),
        sort.dir,
      );
      if (cmp !== 0) return cmp;
      return byDefaultOrder(left.row, right.row);
    });
  } else {
    ordered.sort((left, right) => byDefaultOrder(left.row, right.row));
  }
  const already = ordered.every((unit, index) => unit.row === sortable[index]?.row) && headersPainted(table, sort);
  if (already) return;
  for (const unit of ordered) {
    tbody.appendChild(unit.row);
    for (const follower of unit.followers) tbody.appendChild(follower);
  }
  for (const row of pinned) tbody.appendChild(row);
  paintHeaders(table, sort);
}

function sortCallsPlusGroups(head: HTMLTableElement, sort: HeaderSort | null): void {
  const list = head.parentElement?.querySelector(".sfe-calls-plus-row-list");
  if (!(list instanceof HTMLElement)) {
    paintHeaders(head, sort);
    return;
  }
  const groups = [...list.children].filter((node): node is HTMLElement => node instanceof HTMLElement);
  stampOrder(groups);
  const type = sort ? columnType(headerFor(head, sort.key) ?? document.createElement("th")) : "str";
  const ordered = [...groups];
  if (sort) {
    ordered.sort((left, right) => {
      const cmp = compareValues(
        parseSortText(cellText(left, sort.key), type),
        parseSortText(cellText(right, sort.key), type),
        sort.dir,
      );
      if (cmp !== 0) return cmp;
      return byDefaultOrder(left, right);
    });
  } else {
    ordered.sort(byDefaultOrder);
  }
  const already = ordered.every((group, index) => group === groups[index]) && headersPainted(head, sort);
  if (already) return;
  for (const group of ordered) list.appendChild(group);
  paintHeaders(head, sort);
}

function applyKey(key: string): void {
  const sort = sorts.get(key) ?? null;
  applying = true;
  for (const table of tablesForKey(key)) sortTableRows(table, sort);
  applying = false;
}

function reapplyActiveSorts(): void {
  if (applying) return;
  for (const key of sorts.keys()) applyKey(key);
}

function scheduleReapply(): void {
  if (reapplyQueued || applying) return;
  reapplyQueued = true;
  requestAnimationFrame(() => {
    reapplyQueued = false;
    reapplyActiveSorts();
  });
}

function cycleSort(current: HeaderSort | undefined, key: string): HeaderSort | null {
  if (!current || current.key !== key) return { key, dir: "asc" };
  if (current.dir === "asc") return { key, dir: "desc" };
  return null;
}

function isSortableTable(table: HTMLTableElement): boolean {
  if (table.id === "wheelPositionsTable" || table.dataset.sfeFloatFor === "wheelPositionsTable") return false;
  if (table.closest(".sfe-modal-overlay")) return false;
  return Boolean(table.querySelector("thead th[data-sort], thead th[data-col]"));
}

function resetSortsIn(scope: ParentNode): void {
  const keys = new Set<string>();
  for (const node of scope.querySelectorAll("table")) {
    if (!(node instanceof HTMLTableElement)) continue;
    keys.add(storeKey(node));
  }
  applying = true;
  for (const key of keys) {
    sorts.delete(key);
    for (const table of tablesForKey(key)) sortTableRows(table, null);
  }
  applying = false;
}

export function mountHeaderTableSort(): void {
  if (mounted) return;
  mounted = true;

  document.addEventListener(
    "pointerdown",
    (event) => {
      pointerMoved = false;
      pointerX = event.clientX;
      pointerY = event.clientY;
    },
    true,
  );
  document.addEventListener(
    "pointermove",
    (event) => {
      if (Math.abs(event.clientX - pointerX) > 6 || Math.abs(event.clientY - pointerY) > 6) pointerMoved = true;
    },
    true,
  );

  document.addEventListener("click", (event) => {
    if (pointerMoved) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest("button, a, input, textarea, select, .hint")) return;
    const th = target.closest("th");
    if (!(th instanceof HTMLTableCellElement)) return;
    const table = sourceTable(th);
    if (!table || !isSortableTable(table) || skipHeader(th)) return;
    const key = sortKeyFromHeader(th);
    const store = storeKey(table);
    const next = cycleSort(sorts.get(store), key);
    if (next) sorts.set(store, next);
    else sorts.delete(store);
    applyKey(store);
  });

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest(".sfe-reset-filters, .sfe-mobile-reset-filters");
      if (!(button instanceof HTMLElement)) return;
      const scope =
        button.closest(
          "[data-wheel-view], .sfe-analytics-plus-panel, .sfe-calls-plus, .sfe-agents, .panel",
        ) ?? document.body;
      resetSortsIn(scope);
    },
    true,
  );

  const observer = new MutationObserver(() => {
    if (applying || sorts.size === 0) return;
    scheduleReapply();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
